import { useState } from "react";
import {
  CATEGORY_LABELS,
  getVisibleTilesByCategory,
  TILE_REGISTRY,
} from "./tileRegistry";

// Compact left-nav rail. Each row = tile title + tiny right-aligned
// count, hover highlight, click navigates same as a Phase 0 grid tile
// would. NOT the full DashboardTile card -- the quadrant is for big
// tiles; this is the menu / overflow list.
//
// Count source:
//   - standard tiles use tile.select(data).count
//   - custom-component tiles use tile.compactCount(data) if defined
//     (e.g. MyTime renders the pie in the quadrant but just shows
//     total hours as a number here). Omit -> no badge.

const fmtCount = (v) =>
  v === null || v === undefined || v === "" ? null : String(v);

function getSidebarCount(tile, data) {
  if (tile.component) {
    if (typeof tile.compactCount === "function") {
      return fmtCount(tile.compactCount(data));
    }
    return null;
  }
  if (typeof tile.select === "function") {
    const { count } = tile.select(data) || {};
    return fmtCount(count);
  }
  return null;
}

function SidebarRow({ tile, data, onClick }) {
  const [hover, setHover] = useState(false);
  const count = getSidebarCount(tile, data);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        width: "100%",
        padding: "6px 8px",
        background: hover ? "#eef2f6" : "transparent",
        border: "none",
        borderRadius: "4px",
        textAlign: "left",
        cursor: "pointer",
        fontSize: "13px",
        color: "#222",
        fontFamily: "inherit",
      }}
    >
      <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {tile.title}
      </span>
      {count !== null && (
        <span
          style={{
            fontSize: "11px",
            color: "#5a6470",
            background: hover ? "#fff" : "#f1f3f5",
            padding: "1px 6px",
            borderRadius: "8px",
            minWidth: "20px",
            textAlign: "center",
            fontWeight: 600,
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// Plain action row (Customize / Reset / etc.) -- styled like SidebarRow
// but no count badge, optional active highlight, optional color.
function ActionRow({ label, onClick, active, danger }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        width: "100%",
        padding: "6px 8px",
        background: active
          ? "#1a5fb4"
          : hover
          ? danger
            ? "#fef2f2"
            : "#eef2f6"
          : "transparent",
        border: "none",
        borderRadius: "4px",
        textAlign: "left",
        cursor: "pointer",
        fontSize: "13px",
        color: active ? "white" : danger ? "#a00" : "#222",
        fontFamily: "inherit",
        fontWeight: active ? 600 : "normal",
      }}
    >
      {label}
    </button>
  );
}

export default function DashboardSidebar({
  data,
  dashboardProps,
  isEditingLayout,
  onToggleEdit,
  onResetLayout,
}) {
  const groups = getVisibleTilesByCategory(data, dashboardProps);
  const hasPersonal = groups.some((g) => g.category === "personal");

  const personalActions = onToggleEdit ? (
    <>
      <ActionRow
        label={isEditingLayout ? "Done customizing" : "Customize Homepage"}
        active={isEditingLayout}
        onClick={onToggleEdit}
      />
      {isEditingLayout && onResetLayout && (
        <ActionRow
          label="Reset to default"
          danger
          onClick={onResetLayout}
        />
      )}
    </>
  ) : null;

  return (
    <aside
      style={{
        width: 170,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        paddingTop: "4px",
      }}
    >
      {groups.map(({ category, ids }) => (
        <div key={category}>
          <div
            style={{
              fontSize: "12px",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.7px",
              color: "#0d1117",
              padding: "0 8px",
              marginBottom: "4px",
            }}
          >
            {CATEGORY_LABELS[category]}
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {ids.map((id) => {
              const tile = TILE_REGISTRY[id];
              return (
                <SidebarRow
                  key={id}
                  tile={tile}
                  data={data}
                  onClick={dashboardProps[tile.onClickKey]}
                />
              );
            })}
            {category === "personal" && personalActions}
          </div>
        </div>
      ))}

      {/* If the user has no MyTime (never clocked) the personal group
          is filtered out -- still render the section so the
          Customize action remains reachable. */}
      {!hasPersonal && personalActions && (
        <div>
          <div
            style={{
              fontSize: "12px",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.7px",
              color: "#0d1117",
              padding: "0 8px",
              marginBottom: "4px",
            }}
          >
            {CATEGORY_LABELS.personal}
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {personalActions}
          </div>
        </div>
      )}
    </aside>
  );
}
